// Synchronous shared store so only ONE slide-out nav menu is open at a time.
// A single module-level "active menu id" is the source of truth. Opening any
// menu sets it as active and instantly notifies every other menu to close.
// This runs at click time (no reliance on post-render effect ordering), so
// opening one menu always closes the other.

import { useCallback, useEffect, useState } from 'react';

type Listener = (activeId: string | null) => void;

let activeId: string | null = null;
const listeners = new Set<Listener>();

function setActiveMenu(id: string | null) {
  activeId = id;
  // Snapshot to avoid mutation-during-iteration issues.
  for (const l of Array.from(listeners)) l(activeId);
}

/**
 * Exclusive open/close state for a slide-out menu identified by `id`.
 * Returns `[open, setOpen]`. Setting it open closes any other menu; the same
 * happens in reverse, so at most one registered menu can be open at once.
 */
export function useExclusiveMenu(id: string) {
  const [open, setOpenState] = useState(false);

  useEffect(() => {
    const listener: Listener = (currentActive) => {
      if (currentActive !== id) setOpenState(false);
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
      if (activeId === id) activeId = null;
    };
  }, [id]);

  const setOpen = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      setOpenState((prev) => {
        const value = typeof next === 'function' ? next(prev) : next;
        if (value) {
          // Becoming open: claim active + close everyone else.
          if (activeId !== id) setActiveMenu(id);
        } else if (activeId === id) {
          activeId = null;
        }
        return value;
      });
    },
    [id],
  );

  return [open, setOpen] as const;
}
